import { HYPEREVM_PRODUCTS } from '@/features/evault-details/data/products'
import { Archive, Layers, type LucideIcon } from 'lucide-react'

/**
 * Display keys for every cluster that appears on the Markets Overview tile
 * grid. Fully derived — adding a product with a `cluster` UI block to
 * `HYPEREVM_PRODUCTS` auto-registers the cluster here with no further edits.
 *
 * Ordering:
 *   1. evault-backed products (curated Earn vaults)
 *   2. synthetic `pooled` (Aave v3 pool — not a HYPEREVM_PRODUCTS entry)
 *   3. elend-backed products (in HYPEREVM_PRODUCTS iteration order)
 *   4. synthetic `legacy` (deprecated isolated pairs)
 */
export type ClusterKey = string

function productKeyToClusterKey(productKey: string): string {
  return productKey.replace(/^hypurrfi-/, '')
}

function buildClusterKeys(): ClusterKey[] {
  const keys: ClusterKey[] = []
  const evaultKeys: ClusterKey[] = []
  const elendKeys: ClusterKey[] = []

  for (const [productKey, product] of HYPEREVM_PRODUCTS) {
    if (!product.cluster) continue // product isn't intended to appear as a tile
    const clusterKey = productKeyToClusterKey(productKey)
    if (product.source === 'evault') evaultKeys.push(clusterKey)
    else if (product.source === 'elend') elendKeys.push(clusterKey)
  }

  keys.push(...evaultKeys)
  keys.push('pooled')
  keys.push(...elendKeys)
  keys.push('legacy')
  return keys
}

export const CLUSTER_KEYS: readonly ClusterKey[] = buildClusterKeys()

/**
 * Default ordering for vault rows inside each expanded Markets Overview table.
 * Only addresses listed here are pinned to the top of their market table.
 * Unlisted rows keep the existing table order.
 */
export const MARKET_VAULT_SORT_ORDER: Readonly<Record<ClusterKey, readonly string[]>> = Object.freeze({
  earn: [
    '0xEbbb1e5Dd7E9C4DCa7B7fD4442571cAE5Fb37629', // USDG0
  ],
  prime: [
    '0x8d0dc42992597dc4e46dfcb74990341fd5baee9d', // USDG0
  ],
  pooled: [],
  yield: [
    '0xefc561c796aeb24d09cc35b2029881b483fc1c9e', // USDG0
  ],
  scale: [],
  legacy: [],
})

export function getMarketVaultSortRank(clusterKey: ClusterKey, addresses: readonly (string | undefined)[]): number {
  const order = MARKET_VAULT_SORT_ORDER[clusterKey]
  if (!order) return Number.MAX_SAFE_INTEGER

  let bestRank = Number.MAX_SAFE_INTEGER
  for (const address of addresses) {
    if (!address) continue
    const normalizedAddress = address.toLowerCase()
    const rank = order.findIndex((orderedAddress) => orderedAddress.toLowerCase() === normalizedAddress)
    if (rank !== -1 && rank < bestRank) {
      bestRank = rank
    }
  }
  return bestRank
}

export type MarketGetActionKind = 'usdg0-mint'

export interface MarketGetActionConfig {
  kind: MarketGetActionKind
  label: string
  addresses: readonly string[]
  title: string
  description: string
  offerTokenSymbol: string
  wantTokenSymbol: string
  feeLabel: string
  queueAddress: string
  offerTokenAddress: string
  offerTokenDecimals: number
  wantTokenAddress: string
  wantTokenDecimals: number
}

const USDG0_GET_ACTION: MarketGetActionConfig = {
  kind: 'usdg0-mint',
  label: 'Get USDG0',
  addresses: [
    '0xEbbb1e5Dd7E9C4DCa7B7fD4442571cAE5Fb37629',
    '0x8d0dc42992597dc4e46dfcb74990341fd5baee9d',
    '0xefc561c796aeb24d09cc35b2029881b483fc1c9e',
  ],
  title: 'Get USDG0',
  description: 'Mint USDG0 from USDC through the OneToOneQueue orchestration flow.',
  offerTokenSymbol: 'USDC',
  wantTokenSymbol: 'USDG0',
  feeLabel: '5 bps',
  queueAddress: '0xe87Be03D9E517Cee406C27E3F78e5bAB9151D58A',
  offerTokenAddress: '0xb88339CB7199b77E23DB6E890353E22632Ba630f',
  offerTokenDecimals: 6,
  wantTokenAddress: '0x9522133c663ba8C2a8A7b4F63Dd2aC8DccCe1505',
  wantTokenDecimals: 6,
}

export const MARKET_GET_ACTIONS: Readonly<Record<ClusterKey, readonly MarketGetActionConfig[]>> = Object.freeze({
  earn: [USDG0_GET_ACTION],
  prime: [USDG0_GET_ACTION],
  pooled: [],
  yield: [USDG0_GET_ACTION],
  scale: [],
  legacy: [],
})

export function getMarketGetAction(
  clusterKey: ClusterKey,
  addresses: readonly (string | undefined)[],
): MarketGetActionConfig | undefined {
  const actions = MARKET_GET_ACTIONS[clusterKey]
  if (!actions) return undefined

  const normalizedAddresses = new Set(
    addresses.filter((address): address is string => !!address).map((address) => address.toLowerCase()),
  )

  return actions.find((action) => action.addresses.some((address) => normalizedAddresses.has(address.toLowerCase())))
}

export function findMarketGetAction(addresses: readonly (string | undefined)[]): MarketGetActionConfig | undefined {
  for (const clusterKey of CLUSTER_KEYS) {
    const result = getMarketGetAction(clusterKey, addresses)
    if (result) return result
  }
  return undefined
}

export interface ClusterConfig {
  key: ClusterKey
  name: string
  description: string
  details: string
  icon: LucideIcon
  background: string
  foreground: string
  selectedBorder: string
}

/**
 * Synthetic cluster configs for groupings that aren't backed by a
 * `HYPEREVM_PRODUCTS` entry (Aave pooled market, legacy isolated pairs).
 * Everything else is colocated on the product entry in `products.ts`.
 */
const SYNTHETIC_CLUSTERS: Record<'pooled' | 'legacy', ClusterConfig> = {
  pooled: {
    key: 'pooled',
    name: 'Pooled',
    description: 'Aave v3.02 Pooled Market',
    details: 'Single shared pool for lending and borrowing. Algorithmic rates, deep liquidity, flexible collateral.',
    icon: Layers,
    background: 'bg-[#9DDEF7]/15',
    foreground: 'text-[#9DDEF7]',
    selectedBorder: 'border-[#9DDEF7]/30',
  },
  legacy: {
    key: 'legacy',
    name: 'Legacy',
    description: 'Isolated lending pairs',
    details:
      'Deprecated lending pairs retained for position wind-down only. No new deposits or borrows accepted. Users with open positions are encouraged to repay and transition to current Euler markets.',
    icon: Archive,
    background: 'bg-zinc-500/15',
    foreground: 'text-zinc-400',
    selectedBorder: 'border-zinc-400/20',
  },
}

/**
 * Resolve the `ClusterConfig` for a given key. Product-backed clusters
 * come from `HYPEREVM_PRODUCTS[hypurrfi-${key}].cluster`; the two synthetic
 * entries come from `SYNTHETIC_CLUSTERS`.
 */
function resolveClusterConfig(key: ClusterKey): ClusterConfig {
  if (key === 'pooled' || key === 'legacy') return SYNTHETIC_CLUSTERS[key]
  const product = HYPEREVM_PRODUCTS.get(`hypurrfi-${key}`)
  if (!product?.cluster) {
    throw new Error(`Missing cluster UI config for "${key}" in HYPEREVM_PRODUCTS`)
  }
  return { key, ...product.cluster }
}

export const CLUSTER_CONFIG: Record<string, ClusterConfig> = Object.fromEntries(
  CLUSTER_KEYS.map((key) => [key, resolveClusterConfig(key)]),
)

export function clusterBadgeClasses(clusterKey: ClusterKey): string {
  const config = CLUSTER_CONFIG[clusterKey]
  if (!config) return 'bg-slate-400/15 text-slate-400'
  return `${config.background} ${config.foreground}`
}

/**
 * Minimum opportunity shape needed for cluster matching. Accepts both
 * `YieldOpportunity` (from unified-earn) and adapted shapes like
 * `activityAsOpportunity`. `address` is required for evault matching —
 * without it, multi-evault-cluster setups can't be disambiguated.
 */
export interface ClusterMatchableOpportunity {
  source: string
  market?: { key?: string } | undefined
  /** Vault address — needed to disambiguate between evault clusters. */
  address?: string
}

/**
 * Whether an opportunity belongs to a given cluster. Centralized so feature
 * components don't re-implement the matching rules.
 *
 *  - `pooled` → `source === 'pooled'`
 *  - `legacy` → `source === 'isolated'`
 *  - evault product clusters → `source === 'evault'` AND opportunity's vault
 *    address appears in the product's `vaults` list. Using address membership
 *    (not just source) is intentional: it lets multiple evault clusters
 *    coexist in `HYPEREVM_PRODUCTS` without every evault opportunity matching
 *    every evault tile.
 *  - elend product clusters → `source === 'elend' && market.key === 'hypurrfi-${key}'`
 */
export function matchOpportunityToCluster(opp: ClusterMatchableOpportunity, clusterKey: ClusterKey): boolean {
  if (clusterKey === 'pooled') return opp.source === 'pooled'
  if (clusterKey === 'legacy') return opp.source === 'isolated'

  const product = HYPEREVM_PRODUCTS.get(`hypurrfi-${clusterKey}`)
  if (!product) return false

  if (product.source === 'evault') {
    if (opp.source !== 'evault' || !opp.address) return false
    const normalized = opp.address.toLowerCase()
    return product.vaults.some((v) => v.toLowerCase() === normalized)
  }
  return opp.source === 'elend' && opp.market?.key === `hypurrfi-${clusterKey}`
}

/**
 * First `CLUSTER_KEYS` entry whose cluster this opportunity belongs to, or
 * `undefined` if no cluster matches (e.g. indexer returned a market that's
 * not registered in `HYPEREVM_PRODUCTS`).
 */
export function clusterKeyForOpportunity(opp: ClusterMatchableOpportunity): ClusterKey | undefined {
  for (const key of CLUSTER_KEYS) {
    if (matchOpportunityToCluster(opp, key)) return key
  }
  return undefined
}

/**
 * Display-label → `ClusterKey` lookup, built once at module init from
 * `CLUSTER_CONFIG`. Use when translating a user-selected filter-chip label
 * (e.g. `"Prime"`) back to its cluster key (`"prime"`).
 */
export const CLUSTER_KEY_BY_LABEL: ReadonlyMap<string, ClusterKey> = (() => {
  const map = new Map<string, ClusterKey>()
  for (const key of CLUSTER_KEYS) {
    const config = CLUSTER_CONFIG[key]
    if (config) map.set(config.name, key)
  }
  return map
})()

/**
 * Cluster keys that represent markets eligible for borrow on the EBorrow page.
 *
 * - Excludes any cluster whose `HYPEREVM_PRODUCTS` entry has `source: 'evault'`
 *   — curated earn vaults are deposit-only.
 * - Excludes `legacy` — isolated pairs don't flow through EBorrow's data model
 *   (`pair.routeType` is `'pooled' | 'euler'` only; there's no isolated route
 *   on that page).
 *
 * Adding a new elend cluster to `HYPEREVM_PRODUCTS` automatically produces a
 * new borrow filter chip with no edits here.
 *
 * Lives in this module (not `euler-contracts.ts`) because depending on
 * `HYPEREVM_PRODUCTS` from `euler-contracts.ts` creates a TDZ cycle via
 * `consts.ts`'s re-exports.
 */
export const E_BORROW_CLUSTER_KEYS: readonly ClusterKey[] = CLUSTER_KEYS.filter((key) => {
  if (key === 'legacy') return false
  const product = HYPEREVM_PRODUCTS.get(`hypurrfi-${key}`)
  return product?.source !== 'evault'
})

/**
 * eBorrow category filter labels. Cluster labels derive from `CLUSTER_CONFIG`
 * so renaming a cluster or adding a new one propagates here automatically.
 * The asset-type labels (Stablecoins / LST / Volatile) are fixed — not clusters.
 */
export const E_BORROW_CATEGORIES: string[] = [
  'All',
  ...E_BORROW_CLUSTER_KEYS.map((key) => CLUSTER_CONFIG[key]!.name),
  'Stablecoins',
  'LST',
  'Volatile',
]
